import {
  CLIENT_SIDE_ERROR_CODE,
  ClientError,
} from '@/error-handler/ClientError';

import { getFieldProperty } from '../configs/config-actions';
import { SCRAPER_REQ_FIELD_KEY } from '../configs/scraper-field-map/field-const';
import type { SCRAPER_NAME } from '../types';

export const parseCSVData = (
  data: Record<SCRAPER_REQ_FIELD_KEY, string>[],
  fieldList: SCRAPER_REQ_FIELD_KEY[],
  scraperName: SCRAPER_NAME,
): Record<string, string | number | boolean | string[]>[] => {
  // title을 기준으로 groupby
  const groupedByTitle = new Map<
    string,
    Record<SCRAPER_REQ_FIELD_KEY, string>[]
  >();

  data.forEach((row) => {
    const title = row.title || '';
    if (!groupedByTitle.has(title)) {
      groupedByTitle.set(title, []);
    }
    groupedByTitle.get(title)!.push(row);
  });

  // 각 그룹을 처리하여 최종 결과 생성
  return Array.from(groupedByTitle.values()).map((group) => {
    const parsedRow: Record<string, string | number | boolean | string[]> = {};

    // 첫 번째 row를 기준으로 초기화
    const firstRow = group[0];

    Object.entries(firstRow).forEach(([key, value], index) => {
      try {
        const type = getFieldProperty(key as SCRAPER_REQ_FIELD_KEY, scraperName)
          ?.type;

        if (type === 'string[]') {
          // string[] 타입인 경우 모든 row에서 값을 수집하여 배열로 만듦
          const arrayValues = group
            .map((row) => row[key as SCRAPER_REQ_FIELD_KEY])
            .filter((val) => val && val.trim() !== '') // 빈 값 제거
            .map((val) => val.trim());
          parsedRow[key] = arrayValues;
        } else if (type === 'number') {
          const numberValue = Number(value);
          if (isNaN(numberValue)) throw 'Invalid number value';
          parsedRow[key] = numberValue;
        } else if (type === 'boolean') {
          const normalizedValue = value.trim().toLowerCase();
          if (normalizedValue === 'true') {
            parsedRow[key] = true;
          } else if (normalizedValue === 'false') {
            parsedRow[key] = false;
          } else if (normalizedValue !== '') {
            throw 'Invalid boolean value';
          }
        } else {
          parsedRow[key] = value;
        }
      } catch (e) {
        if (typeof e === 'string') {
          throw new ClientError(
            CLIENT_SIDE_ERROR_CODE.CSV_UPLOAD_ERROR,
            `[Row : ${index}, Field : ${key}] ${e}`,
          );
        }
        throw e;
      }
    });

    validateRequiredFields(parsedRow, fieldList, scraperName);

    return parsedRow;
  });
};

const validateRequiredFields = (
  record: Record<string, string | number | boolean | string[]>,
  fieldList: SCRAPER_REQ_FIELD_KEY[],
  scraperName: SCRAPER_NAME,
) => {
  const mutatedFieldList = [...fieldList, SCRAPER_REQ_FIELD_KEY.TITLE];
  mutatedFieldList.forEach((field) => {
    const fieldConfig = getFieldProperty(field, scraperName);
    if (!fieldConfig?.required) return;

    if (isMissingRequiredValue(record[field], fieldConfig.type)) {
      throw new ClientError(
        CLIENT_SIDE_ERROR_CODE.CSV_UPLOAD_ERROR,
        getRequiredFieldErrorMessage(field),
      );
    }
  });
};

const isMissingRequiredValue = (
  value: string | number | boolean | string[] | undefined,
  type: 'string' | 'string[]' | 'boolean' | 'number',
) => {
  if (type === 'string') {
    return typeof value !== 'string' || value.trim() === '';
  }

  if (type === 'string[]') {
    return !Array.isArray(value) || value.length === 0;
  }

  if (type === 'number') {
    return typeof value !== 'number' || Number.isNaN(value);
  }

  if (type === 'boolean') {
    return typeof value !== 'boolean';
  }

  return typeof value === 'undefined';
};

const getRequiredFieldErrorMessage = (field: SCRAPER_REQ_FIELD_KEY) => {
  if (field === SCRAPER_REQ_FIELD_KEY.TITLE) {
    return 'Please add a Title column to your CSV file and make sure every row has a title.';
  }

  return `${field} field is required`;
};
